import React, { useEffect, useState } from 'react';
import { APP_CONFIG } from 'Config';
import Reaptcha from 'reaptcha';
import { useFormikContext } from 'formik';
import { useGlobalContext } from 'context/GlobalContext';
import Link from 'components/Link';
import { Input } from 'components/partials/Fields/FormikInput';
import Button from 'components/Elements/Button';
import { getQueryParam } from 'helpers/queryParams';
import './styles.css';
import { useLoginContext } from '../../_context/useLoginContext';

const ACTIVATION_MESSAGES = {
    BAD_CALL: { title: 'Oops!', body: 'Something happened. Please try again.', type: 'warning' },
    TOKEN_USED: { title: 'Oops!', body: 'Confirmation link is no longer valid.', type: 'warning' },
    BAD_TOKEN: {
        title: 'Oops!',
        body: 'There was a problem confirming your email, please contact support.',
        type: 'warning',
    },
    OK: { title: 'Success!', body: 'You have confirmed your email.', type: 'success' },
};

const LOGIN_ERROR_MESSAGES = {
    INVALID_CREDENTIALS: {
        message: 'Incorrect login credentials. Please try again.',
    },
    ACCOUNT_INCOMPLETE: {
        message: 'Sorry, Auctioneer user type is not supported anymore. For help,',
        showContactInfo: true,
    },
    ACCOUNT_DEACTIVATED: {
        message: 'Sorry, your account has been deactivated. For more information, For help,',
        showContactInfo: true,
    },
    VERIFICATION_REJECTED: {
        message: 'Your identity verification has been rejected.',
    },
    VERIFICATION_PENDING: {
        message: 'Customer Support will review your submitted information within 24-48 hours and will keep you posted on your registration through email. Please do not contact Customer Support until the reviewing time window is expired.',
    },
    EMAIL_UNVERIFIED: {
        message: 'Your email has not been confirmed yet. Please check for an email from StorageTreasures containing a confirmation link.',
    },
};

const Alert = ({ type = 'warning', title, children }) => (
    <div className={`alert alert-${type}`}>
        {title && <strong>{title}</strong>}
        {' '}
        {children}
    </div>
);

const LoginError = ({ message, showContactInfo }) => (
    <Alert type="warning" title="Oops!">
        {message}
        {showContactInfo && (
            <> <a href="https://help.storagetreasures.com/hc/en-us/requests/new">
                https://help.storagetreasures.com/hc/en-us/requests/new
            </a>.</>
        )}
    </Alert>
);

const ActivationAlert = ({ message }) => {
    const alertConfig = ACTIVATION_MESSAGES[message?.toUpperCase()];
    if (!alertConfig) return null;

    return (
        <Alert type={alertConfig.type} title={alertConfig.title}>
            {alertConfig.body}
        </Alert>
    );
};

export const LoginForm = ({ loading, isValidCredentials }) => {
    const { user } = useGlobalContext();
    const {
        isVerificationPending,
        isVerificationRejected,
        isAccountIncomplete,
        isEmailVerified,
        checkEmailVerified,
    } = useLoginContext();
    const { values, setFieldValue } = useFormikContext();
    const [showPassword, setShowPassword] = useState(false);
    const [captcha, setCaptcha] = useState(false);

    const getLoginErrorProps = () => {
        if (!isValidCredentials) return LOGIN_ERROR_MESSAGES['INVALID_CREDENTIALS'];
        if (isAccountIncomplete) return LOGIN_ERROR_MESSAGES['ACCOUNT_INCOMPLETE'];
        if (user.user_id && !user.active) return LOGIN_ERROR_MESSAGES['ACCOUNT_DEACTIVATED'];
        if (isVerificationRejected) return LOGIN_ERROR_MESSAGES['VERIFICATION_REJECTED'];
        if (isVerificationPending) return LOGIN_ERROR_MESSAGES['VERIFICATION_PENDING'];
        if (!isEmailVerified) return LOGIN_ERROR_MESSAGES['EMAIL_UNVERIFIED'];
    };

    useEffect(() => {
        if (!isValidCredentials) {
            setFieldValue('password', '');
        }
    }, [isValidCredentials, setFieldValue]);

    const errorProps = getLoginErrorProps();
    const isLocalEnv = APP_CONFIG.APP_ENV === 'local';
    const isSubmitDisabled = loading || (!isLocalEnv && !captcha);

    return (
        <>
            <ActivationAlert message={getQueryParam('activate_message')}/>
            {errorProps && <LoginError {...errorProps} />}
            <Input
                formik
                name="email"
                label="Email Address"
                onBlur={checkEmailVerified}
            />
            <Input
                formik
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="Password"
                onDoubleClick={() => setShowPassword(!showPassword)}
                help="Your password is case-sensitive"
            />
            <div className="text-center">
                <div style={{ margin: '0 auto', width: 305 }}>
                    {isLocalEnv && (
                        <div>Login would be {captcha ? 'enabled' : 'disabled'}</div>
                    )}
                    <Reaptcha
                        sitekey={APP_CONFIG.Services.google_recaptcha.site_key}
                        onVerify={setCaptcha}
                    />
                </div>
                <Button
                    type="submit"
                    className="my-4 w-100 btn-regular"
                    disabled={isSubmitDisabled}
                >
                    {loading && <i className="fa fa-spin fa-spinner"/>}
                    {' '}
                    Log In
                </Button>
                <div>
                    <Link
                        to={`/account/reset-password${values?.email ? `?email=${encodeURIComponent(values?.email)}` : ''}`}
                        className="font-size-16 mb-2"
                    >
                        Forgot your Password?
                    </Link>
                </div>

                <div className="hr">
                    <span className="text">New to StorageTreasures?</span>
                </div>
                <Link
                    to="/register"
                    id="login-register"
                    className="btn w-100 btn-orange btn-regular"
                >
                    Create an Account
                </Link>
            </div>
        </>
    );
};
